import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, X, Sparkles, Square, Eye } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const emptyFromFields = (fields) => {
  const o = {};
  fields.forEach((f) => {
    if (f.type === "checkbox") o[f.key] = f.default ?? true;
    else if (f.type === "number") o[f.key] = f.default ?? 0;
    else if (f.type === "json") o[f.key] = "";
    else o[f.key] = "";
  });
  return o;
};

const resolveImageUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u}`;
};

const AI_DEFAULT_PROMPT =
  "Take this copy and rework into a constructive, objective news story of 240 words. Automatically italicise key phrases through the copy.";

const AiTextareaField = ({ value, onChange, testKey, defaultPrompt, rows = 8 }) => {
  const [prompt, setPrompt] = useState(defaultPrompt || AI_DEFAULT_PROMPT);
  const [busy, setBusy] = useState(false);
  const [ctrl, setCtrl] = useState(null);

  const stop = () => {
    if (ctrl) ctrl.abort();
    setBusy(false);
  };

  const rewrite = async () => {
    const source = (value || "").trim();
    if (!source) return toast.error("Please write some source copy first.");
    if (!prompt.trim()) return toast.error("Please give the AI a prompt.");

    const controller = new AbortController();
    setCtrl(controller);
    setBusy(true);
    let accumulated = "";
    try {
      const token = localStorage.getItem("wm_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/ai/rewrite`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({ text: source, prompt }),
        signal: controller.signal,
      });
      if (!res.ok) {
        const err = await res.text();
        throw new Error(err || `HTTP ${res.status}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      onChange("");
      while (true) {
        const { done, value: chunk } = await reader.read();
        if (done) break;
        accumulated += decoder.decode(chunk, { stream: true });
        onChange(accumulated);
      }
      toast.success("Rewritten with Claude");
    } catch (err) {
      if (err.name === "AbortError") {
        toast.message("Rewrite stopped");
      } else {
        toast.error(err.message || "AI rewrite failed");
      }
    } finally {
      setBusy(false);
      setCtrl(null);
    }
  };

  return (
    <div className="mt-2 space-y-4" data-testid={`field-${testKey}`}>
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        data-testid={`ai-body-${testKey}`}
        className="bg-background border-border rounded-none font-serif text-base leading-relaxed"
        placeholder="Draft your source copy here — then let the AI polish it below…"
      />
      <div className="border border-dashed border-primary/40 bg-primary/5 p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-primary" />
          <span className="font-mono text-xs uppercase tracking-widest text-primary">AI editorial rewrite</span>
        </div>
        <Textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={2}
          data-testid={`ai-prompt-${testKey}`}
          className="bg-background border-border rounded-none text-sm"
          placeholder="Prompt for the AI"
        />
        <div className="flex flex-wrap items-center gap-3">
          {busy ? (
            <button
              type="button"
              onClick={stop}
              data-testid={`ai-stop-${testKey}`}
              className="inline-flex items-center gap-2 bg-ink text-white rounded-none font-mono text-xs uppercase tracking-widest px-4 py-2 hover:bg-destructive"
            >
              <Square size={14} /> Stop
            </button>
          ) : (
            <button
              type="button"
              onClick={rewrite}
              data-testid={`ai-run-${testKey}`}
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-none font-mono text-xs uppercase tracking-widest px-4 py-2 hover:bg-primary/90 disabled:opacity-40"
              disabled={busy}
            >
              <Sparkles size={14} /> Rewrite with AI
            </button>
          )}
          <span className="font-sans text-xs text-muted-foreground">
            Replaces the body with the AI output. Uses <code>&lt;em&gt;</code> tags so key phrases render italic on the site.
          </span>
        </div>
      </div>
    </div>
  );
};


const ImageField = ({ value, onChange, testKey, hint }) => {
  const [busy, setBusy] = useState(false);
  const [dims, setDims] = useState(null);
  useEffect(() => setDims(null), [value]);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Please choose an image file.");
    if (f.size > 8 * 1024 * 1024) return toast.error("Image must be under 8 MB.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("image", f);
      const { data } = await api.post("/admin/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <div className="mt-2 space-y-3" data-testid={`field-${testKey}`}>
      {value && (
        <div className="relative inline-block">
          <img
            src={resolveImageUrl(value)}
            alt="preview"
            className="max-h-40 rounded border border-border bg-secondary"
            onLoad={(e) => setDims(`${e.currentTarget.naturalWidth} × ${e.currentTarget.naturalHeight} px`)}
          />
          {dims && (
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1.5" data-testid={`dims-${testKey}`}>
              {dims}
            </p>
          )}
          <button type="button" onClick={() => onChange("")} data-testid={`clear-${testKey}`} className="absolute -top-2 -right-2 bg-destructive text-white rounded-full p-1 shadow" title="Remove image">
            <X size={14} />
          </button>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <label className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-none font-mono text-xs uppercase tracking-widest px-4 py-2 cursor-pointer hover:bg-primary/90">
          <Upload size={14} /> {busy ? "Uploading…" : value ? "Replace image" : "Upload image"}
          <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid={`upload-${testKey}`} disabled={busy} />
        </label>
        <Input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="…or paste an image URL"
          data-testid={`url-${testKey}`}
          className="flex-1 min-w-[220px] bg-background border-border rounded-none"
        />
      </div>
      {hint && <p className="font-sans text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
};

const CrudManager = ({ title, endpoint, fields, columns, items, reload, previewUrl }) => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const openNew = () => {
    setEditing(null);
    setForm(emptyFromFields(fields));
    setOpen(true);
  };
  const openEdit = (item) => {
    setEditing(item);
    const f = {};
    fields.forEach((fl) => {
      const raw = item[fl.key];
      if (fl.type === "json") {
        f[fl.key] = raw != null ? JSON.stringify(raw, null, 2) : "";
      } else {
        f[fl.key] = raw ?? emptyFromFields(fields)[fl.key];
      }
    });
    setForm(f);
    setOpen(true);
  };

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  /** Build the API payload from the current form. Returns [payload, error?]. */
  const buildPayload = () => {
    const payload = { ...form };
    for (const f of fields) {
      if (f.type === "number") payload[f.key] = Number(payload[f.key]) || 0;
      if (f.type === "json") {
        const raw = (payload[f.key] || "").trim();
        if (!raw) {
          // Preserve explicit `jsonDefault: null` (used for Optional[dict] fields
          // such as hero_animation/access/ad_guidelines). `??` would swap null
          // for [] and cause a 422 "Input should be a valid dictionary".
          payload[f.key] = Object.prototype.hasOwnProperty.call(f, "jsonDefault") ? f.jsonDefault : [];
        } else {
          try {
            payload[f.key] = JSON.parse(raw);
          } catch (e) {
            return [null, `${f.label}: invalid JSON — ${e.message}`];
          }
        }
      }
    }
    return [payload, null];
  };

  const save = async () => {
    setSaving(true);
    try {
      const [payload, err] = buildPayload();
      if (err) {
        toast.error(err);
        setSaving(false);
        return;
      }
      if (editing) await api.put(`/admin/${endpoint}/${editing.id}`, payload);
      else await api.post(`/admin/${endpoint}`, payload);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false);
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const preview = () => {
    const [payload, err] = buildPayload();
    if (err) {
      toast.error(err);
      return;
    }
    const url = previewUrl && previewUrl(editing, payload);
    if (!url) {
      toast.error("No public page available to preview yet — save first, then use Preview.");
      return;
    }
    const key = `wm_draft_${endpoint}_${editing?.id || "new"}_${Date.now()}`;
    try {
      // localStorage (not sessionStorage) so the draft is readable in the new tab.
      // Payload includes a timestamp for TTL cleanup on the reading side.
      localStorage.setItem(
        key,
        JSON.stringify({
          ...(editing || {}),
          ...payload,
          _preview: true,
          _createdAt: Date.now(),
        }),
      );
    } catch (e) {
      toast.error("Could not store draft — browser storage full?");
      return;
    }
    const separator = url.includes("?") ? "&" : "?";
    const previewHref = `${url}${separator}preview=${encodeURIComponent(key)}`;
    window.open(previewHref, "_blank", "noopener,noreferrer");
    toast.success("Preview opened in a new tab.");
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item[columns[0].key]}"?`)) return;
    try {
      await api.delete(`/admin/${endpoint}/${item.id}`);
      toast.success("Deleted");
      reload();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid={`crud-${endpoint}`}>
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-serif text-3xl md:text-4xl font-light">{title}</h2>
        <Button
          onClick={openNew}
          data-testid={`add-${endpoint}`}
          className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono text-xs uppercase tracking-widest"
        >
          <Plus size={16} className="mr-2" /> New
        </Button>
      </div>

      <div className="border border-border">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              {columns.map((c) => (
                <TableHead key={c.key} className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  {c.label}
                </TableHead>
              ))}
              <TableHead className="text-right font-mono text-xs uppercase tracking-widest text-muted-foreground">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(items || []).map((item) => (
              <TableRow key={item.id} className="border-border" data-testid={`row-${endpoint}-${item.id}`}>
                {columns.map((c) => (
                  <TableCell key={c.key} className="font-light max-w-xs truncate">
                    {c.render ? c.render(item[c.key], item) : String(item[c.key] ?? "—")}
                  </TableCell>
                ))}
                <TableCell className="text-right whitespace-nowrap">
                  <button
                    onClick={() => openEdit(item)}
                    data-testid={`edit-${endpoint}-${item.id}`}
                    className="text-muted-foreground hover:text-primary p-2"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => remove(item)}
                    data-testid={`delete-${endpoint}-${item.id}`}
                    className="text-muted-foreground hover:text-destructive p-2"
                  >
                    <Trash2 size={16} />
                  </button>
                </TableCell>
              </TableRow>
            ))}
            {(!items || items.length === 0) && (
              <TableRow>
                <TableCell colSpan={columns.length + 1} className="text-center py-10 text-muted-foreground font-light">
                  No entries yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`dialog-${endpoint}`}>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light">
              {editing ? "Edit" : "New"} {title.replace(/s$/, "")}
            </DialogTitle>
            <DialogDescription className="font-sans text-xs text-muted-foreground">
              Fill in the fields below and save to publish changes to the live site.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-2">
            {fields.map((f) => (
              <div key={f.key}>
                <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  {f.label}
                </label>
                {f.type === "textarea" ? (
                  <Textarea
                    value={form[f.key] || ""}
                    onChange={(e) => set(f.key, e.target.value)}
                    data-testid={`field-${f.key}`}
                    className="mt-2 bg-background border-border rounded-none"
                    rows={3}
                  />
                ) : f.type === "json" ? (
                  <div className="mt-2 space-y-2" data-testid={`field-${f.key}`}>
                    <Textarea
                      value={form[f.key] || ""}
                      onChange={(e) => set(f.key, e.target.value)}
                      data-testid={`json-${f.key}`}
                      className="bg-background border-border rounded-none font-mono text-xs leading-relaxed"
                      rows={f.rows || 8}
                      placeholder={f.placeholder || ""}
                      spellCheck={false}
                    />
                    {f.hint && <p className="font-sans text-xs text-muted-foreground">{f.hint}</p>}
                  </div>
                ) : f.type === "ai-textarea" ? (
                  <AiTextareaField
                    value={form[f.key] || ""}
                    onChange={(v) => set(f.key, v)}
                    testKey={f.key}
                    defaultPrompt={f.aiPrompt}
                    rows={f.rows || 8}
                  />
                ) : f.type === "select" ? (
                  <Select value={form[f.key] || ""} onValueChange={(v) => set(f.key, v)}>
                    <SelectTrigger data-testid={`field-${f.key}`} className="mt-2 bg-background border-border rounded-none">
                      <SelectValue placeholder="Select…" />
                    </SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                      {f.options.map((o) => (
                        <SelectItem key={o} value={o}>
                          {o}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : f.type === "checkbox" ? (
                  <div className="mt-3 flex items-center gap-3">
                    <Checkbox
                      checked={!!form[f.key]}
                      onCheckedChange={(v) => set(f.key, !!v)}
                      data-testid={`field-${f.key}`}
                      className="rounded-none border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                    />
                    <span className="text-sm text-foreground/70">{f.hint || "Enabled"}</span>
                  </div>
                ) : f.type === "image" ? (
                  <ImageField
                    value={form[f.key] || ""}
                    onChange={(v) => set(f.key, v)}
                    testKey={f.key}
                    hint={f.hint}
                  />
                ) : (
                  <Input
                    type={f.type === "number" ? "number" : "text"}
                    value={form[f.key] ?? ""}
                    onChange={(e) => set(f.key, e.target.value)}
                    data-testid={`field-${f.key}`}
                    className="mt-2 bg-background border-border rounded-none"
                  />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            {previewUrl && (
              <Button
                type="button"
                onClick={preview}
                disabled={saving}
                data-testid={`preview-${endpoint}`}
                variant="outline"
                className="border-ink text-ink hover:bg-ink hover:text-white rounded-none font-mono text-xs uppercase tracking-widest"
              >
                <Eye size={14} className="mr-2" /> Preview
              </Button>
            )}
            <Button
              onClick={save}
              disabled={saving}
              data-testid={`save-${endpoint}`}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono text-xs uppercase tracking-widest"
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CrudManager;
